import React from 'react';
import { Box, Typography } from '@mui/material';
import chipIcon from '../../assets/images/chip_icon.svg';
import contactlessIcon from '../../assets/images/contactless_icon.svg';

export default function CreditCard({ card }) {
  // Função para formatar o número do cartão
  const formatCardNumber = (number) => {
    const defaultPattern = '•••• •••• •••• ••••';
    if (!number) return defaultPattern;
    
    // Remove espaços e caracteres não numéricos
    const cleanNumber = number.replace(/\D/g, '');
    
    // Preenche com • até completar 16 dígitos
    const paddedNumber = cleanNumber.padEnd(16, '•');
    
    // Agrupa em blocos de 4 e junta com espaços
    return paddedNumber.match(/.{1,4}/g).join(' ');
  };

  // Função para formatar a data
  const formatExpiry = (expiry) => {
    const defaultPattern = 'MM/AA';
    if (!expiry) return defaultPattern;
    
    // Remove qualquer caractere não numérico e a barra
    const numbers = expiry.replace(/[^\d]/g, '');
    
    // Formata mês
    const month = numbers.slice(0, 2).padEnd(2, 'M');
    // Formata ano
    const year = numbers.length > 2 ? numbers.slice(2, 4).padEnd(2, 'A') : 'AA';
    
    return `${month}/${year}`;
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '380px', md: '340px' },
        height: { xs: '240px', md: '220px' },
        margin: '0 auto',
        position: 'relative',
        perspective: '1000px',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
        '&:hover': {
          transform: 'scale(1.02) translateY(-4px)',
          filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.2))',
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: card.focus === 'cvv' ? 'rotateY(180deg)' : 'rotateY(0)',
        }}
      >
        {/* Frente do cartão */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            backfaceVisibility: 'hidden',
            borderRadius: '15px',
            padding: '25px',
            background: 'conic-gradient(from 169.88deg at 98.53% -2.14%, #3E6D6D -9.14deg, #020303 115.1deg, #67CFFC 251.02deg, #3E6D6D 350.86deg, #020303 475.1deg)',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '15px',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
              pointerEvents: 'none',
            }
          }}
        >
          {/* Área dos ícones */}
          <Box sx={{ 
            position: 'relative', 
            height: '60px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1
          }}>
            {/* Ícone de aproximação */}
            <Box
              component="img"
              src={contactlessIcon}
              alt="Contactless"
              sx={{
                width: '30px',
                height: '30px',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
                ml: '7.5px'
              }}
            />
            
            {/* Chip do cartão */}
            <Box
              component="img"
              src={chipIcon}
              alt="Chip"
              sx={{
                width: '45px',
                height: '35px',
                objectFit: 'contain'
              }}
            />
          </Box>

          {/* Número do cartão */}
          <Typography
            sx={{
              color: '#fff',
              fontSize: { xs: '24px', md: '20px' },
              letterSpacing: '2px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
              fontFamily: 'monospace',
              mb: 2,
              textAlign: 'center',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              gap: '12px', // Espaçamento entre os grupos
              '& .number-group': {
                display: 'flex',
                gap: '2px', // Espaçamento entre números do mesmo grupo
              }
            }}
          >
            {formatCardNumber(card.number)
              .split(' ')
              .map((group, groupIndex) => (
                <span key={groupIndex} className="number-group">
                  {group.split('').map((char, charIndex) => (
                    <span 
                      key={charIndex}
                      style={{
                        display: 'inline-block',
                        width: '0.6em',
                        textAlign: 'center',
                        transition: 'opacity 0.2s ease',
                        transitionDelay: `${(groupIndex * 4 + charIndex) * 30}ms`,
                        opacity: 0.9,
                      }}
                    >
                      {char}
                    </span>
                  ))}
                </span>
              ))}
          </Typography>

          {/* Nome e Validade */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end'
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: '#fff',
                  opacity: 0.8,
                  fontSize: '12px',
                  mb: 0.5
                }}
              >
                NOME NO CARTÃO
              </Typography>
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: { xs: '16px', md: '13px' },
                  textTransform: 'uppercase',
                  fontFamily: 'monospace',
                  letterSpacing: '1px'
                }}
              >
                {card.name || 'NOME NO CARTÃO'}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  color: '#fff',
                  opacity: 0.8,
                  fontSize: '12px',
                  mb: 0.5
                }}
              >
                VALIDADE
              </Typography>
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: { xs: '16px', md: '13px' },
                  fontFamily: 'monospace'
                }}
              >
                {formatExpiry(card.expiry)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Verso do cartão */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            backfaceVisibility: 'hidden',
            borderRadius: '15px',
            background: 'conic-gradient(from 169.88deg at 98.53% -2.14%, #3E6D6D -9.14deg, #020303 115.1deg, #67CFFC 251.02deg, #3E6D6D 350.86deg, #020303 475.1deg)',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.2)',
            transform: 'rotateY(180deg)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '15px',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
              pointerEvents: 'none',
            }
          }}
        >
          {/* Faixa magnética */}
          <Box
            sx={{
              width: '100%',
              height: '40px',
              background: '#000',
              mt: '20px'
            }}
          />

          {/* Área do CVV */}
          <Box
            sx={{
              position: 'relative',
              mt: 4,
              mx: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '60px',
              }}
            >
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: '12px',
                  mb: 1,
                }}
              >
                CVV
              </Typography>
              <Box
                sx={{
                  background: '#fff',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'monospace',
                    letterSpacing: '2px',
                    fontSize: '14px',
                    textAlign: 'center',
                    '& > span': {
                      display: 'inline-block',
                      width: '8px',
                      textAlign: 'center'
                    }
                  }}
                >
                  {(card.cvv || '•••').split('').map((char, i) => (
                    <span key={i}>{char}</span>
                  ))}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}